import React, { useState } from 'react';
import {
  Cog6ToothIcon, WrenchScrewdriverIcon, CurrencyRupeeIcon,
  TruckIcon, ShieldCheckIcon, SparklesIcon, CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '../../hooks';
import { adminAPI } from '../../services/api';
import {
  Card, SectionHeader, Button, Badge, StatusBadge,
  Input, Select, Textarea, Toggle, HighRiskActionModal
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

export default function SystemConfiguration() {
  const [activeSection, setActiveSection] = useState('general');
  const [highRiskModalOpen, setHighRiskModalOpen] = useState(false);
  const [pendingUpdateKey, setPendingUpdateKey] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  const { data: settingsData, loading, refetch } = useQuery(
    () => adminAPI.getSettings(),
    []
  );

  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      platform_name: 'Bellezbuy Marketplace',
      support_email: 'support@bellezbuy.com',
      default_commission_rate: 10,
      auto_approve_products: false,
      enable_seller_signup: true,
      maintenance_mode: false,
      default_tax_slab: 18,
      free_shipping_threshold: 999,
      return_window_days: 7,
    }
  });

  const onSubmitSection = (sectionKey, values) => {
    setPendingUpdateKey(sectionKey);
    setPendingValues(values);
    setHighRiskModalOpen(true);
  };

  const handleConfirmSave = async ({ reason, idempotencyKey }) => {
    setLoadingAction(true);
    try {
      await adminAPI.updateSetting(
        pendingUpdateKey,
        {
          value: pendingValues,
          reason,
        },
        {
          headers: { 'Idempotency-Key': idempotencyKey }
        }
      );
      toast.success('System configuration updated and validated');
      setHighRiskModalOpen(false);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.message || 'Configuration validation error');
    } finally {
      setLoadingAction(false);
    }
  };

  const SECTIONS = [
    { key: 'general', label: 'Platform Identity', icon: Cog6ToothIcon },
    { key: 'commission', label: 'Commission & Fees', icon: CurrencyRupeeIcon },
    { key: 'tax_shipping', label: 'Tax & Logistics', icon: TruckIcon },
    { key: 'policies', label: 'Returns & Policies', icon: ShieldCheckIcon },
    { key: 'features', label: 'Feature Flags', icon: SparklesIcon },
  ];

  return (
    <div className="space-y-6 animate-in">
      <SectionHeader
        title="System Configuration & Business Rules"
        subtitle="Platform governance rules, tax slabs, commission engine parameters, and strict schema-validated policies"
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Navigation Sidebar */}
        <Card className="p-2 lg:col-span-1 h-fit space-y-1">
          {SECTIONS.map((sec) => {
            const Icon = sec.icon;
            const isActive = activeSection === sec.key;
            return (
              <button
                key={sec.key}
                onClick={() => setActiveSection(sec.key)}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                  isActive ? 'bg-brand-600 text-white shadow-sm' : 'text-surface-600 hover:bg-surface-100'
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{sec.label}</span>
              </button>
            );
          })}
        </Card>

        {/* Configuration Panel */}
        <Card className="lg:col-span-3 p-6">
          {/* General Section */}
          {activeSection === 'general' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-bold text-surface-900">Platform Identity & Localization</h3>
                <p className="text-xs text-surface-400 mt-0.5">Basic platform credentials, branding and contact channels</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Marketplace Brand Name" {...register('platform_name')} />
                <Input label="Primary Support Email" type="email" {...register('support_email')} />
              </div>
              <div className="pt-4 border-t border-surface-100 flex justify-end">
                <Button variant="primary" size="sm" onClick={() => onSubmitSection('general', { platform_name: watch('platform_name'), support_email: watch('support_email') })}>
                  Save Identity Rules
                </Button>
              </div>
            </div>
          )}

          {/* Commission Section */}
          {activeSection === 'commission' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-bold text-surface-900">Marketplace Commission Engine</h3>
                <p className="text-xs text-surface-400 mt-0.5">Baseline platform commission deducted before seller payout settlement</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Default Platform Commission (%)" type="number" step="0.5" {...register('default_commission_rate')} />
                <div className="p-3.5 rounded-xl bg-surface-50 border border-surface-200 text-xs text-surface-600">
                  <p className="font-semibold text-surface-800">Note on Overrides</p>
                  <p className="mt-0.5">Individual seller-specific commission overrides in the Sellers module supersede this default rate.</p>
                </div>
              </div>
              <div className="pt-4 border-t border-surface-100 flex justify-end">
                <Button variant="primary" size="sm" onClick={() => onSubmitSection('commission', { default_commission_rate: watch('default_commission_rate') })}>
                  Update Commission Rules
                </Button>
              </div>
            </div>
          )}

          {/* Tax & Logistics Section */}
          {activeSection === 'tax_shipping' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-bold text-surface-900">Tax & Logistics Parameters</h3>
                <p className="text-xs text-surface-400 mt-0.5">Default GST slabs and threshold for complimentary customer delivery</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select label="Default GST Rate" {...register('default_tax_slab')}>
                  <option value="0">0% (Exempt)</option>
                  <option value="5">5% (Essentials)</option>
                  <option value="12">12% (Standard I)</option>
                  <option value="18">18% (Standard II)</option>
                  <option value="28">28% (Luxury / Electronics)</option>
                </Select>
                <Input label="Free Shipping Threshold (₹)" type="number" {...register('free_shipping_threshold')} />
              </div>
              <div className="pt-4 border-t border-surface-100 flex justify-end">
                <Button variant="primary" size="sm" onClick={() => onSubmitSection('tax_shipping', { default_tax_slab: watch('default_tax_slab'), free_shipping_threshold: watch('free_shipping_threshold') })}>
                  Save Tax & Shipping Rules
                </Button>
              </div>
            </div>
          )}

          {/* Returns & Policies Section */}
          {activeSection === 'policies' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-bold text-surface-900">Return & Customer Protection Policies</h3>
                <p className="text-xs text-surface-400 mt-0.5">Return eligibility window and customer satisfaction thresholds</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Return Window (Days from delivery)" type="number" {...register('return_window_days')} />
              </div>
              <div className="pt-4 border-t border-surface-100 flex justify-end">
                <Button variant="primary" size="sm" onClick={() => onSubmitSection('policies', { return_window_days: watch('return_window_days') })}>
                  Save Policy Rules
                </Button>
              </div>
            </div>
          )}

          {/* Feature Flags Section */}
          {activeSection === 'features' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-bold text-surface-900">Platform Feature Flags & Gateways</h3>
                <p className="text-xs text-surface-400 mt-0.5">Instant live operational switches across the entire storefront</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl border border-surface-200">
                  <div>
                    <p className="font-semibold text-xs text-surface-900">Public Seller Onboarding</p>
                    <p className="text-[11px] text-surface-500">Allow new merchants to register from the landing page</p>
                  </div>
                  <Toggle checked={watch('enable_seller_signup')} onChange={(e) => setValue('enable_seller_signup', e.target.checked)} />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl border border-surface-200">
                  <div>
                    <p className="font-semibold text-xs text-surface-900">Auto-Approve Products</p>
                    <p className="text-[11px] text-surface-500">Bypass moderation queue and publish products immediately</p>
                  </div>
                  <Toggle checked={watch('auto_approve_products')} onChange={(e) => setValue('auto_approve_products', e.target.checked)} />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl border border-danger-200 bg-danger-50/30">
                  <div>
                    <p className="font-semibold text-xs text-danger-900">Platform Maintenance Mode</p>
                    <p className="text-[11px] text-danger-700">Temporarily block customer checkout for infrastructure upgrades</p>
                  </div>
                  <Toggle checked={watch('maintenance_mode')} onChange={(e) => setValue('maintenance_mode', e.target.checked)} />
                </div>
              </div>
              <div className="pt-4 border-t border-surface-100 flex justify-end">
                <Button variant="danger" size="sm" onClick={() => onSubmitSection('features', { enable_seller_signup: watch('enable_seller_signup'), auto_approve_products: watch('auto_approve_products'), maintenance_mode: watch('maintenance_mode') })}>
                  Commit Feature Flags
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* High-Risk Setting Commit Modal */}
      <HighRiskActionModal
        open={highRiskModalOpen}
        onClose={() => setHighRiskModalOpen(false)}
        onConfirm={handleConfirmSave}
        title="Commit System Configuration Mutation"
        danger={pendingUpdateKey === 'features'}
        confirmLabel="Commit System Settings"
        warningMessage="Altering system-level parameters impacts platform operations, financial deductions, and storefront checkout."
        entitySummary={
          <div>
            <p className="font-bold text-surface-900">Target Config Key: <span className="font-mono text-brand-700">{pendingUpdateKey}</span></p>
            <pre className="mt-1.5 p-2 bg-white rounded border border-surface-200 font-mono text-[10px] max-h-28 overflow-x-auto">
              {JSON.stringify(pendingValues || {}, null, 2)}
            </pre>
          </div>
        }
        loading={loadingAction}
      />
    </div>
  );
}
